import React, { useState } from "react";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Fade,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Link,
} from "@mui/material";
import {
  generateWorkflow,
  createWorkflow,
  updateWorkflow,
  refineWorkflowInPlace,
} from "../../api/agentApi";
import { useLocation, useNavigate } from "react-router-dom";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import WorkflowRefinementEditor from "./WorkflowRefinementEditor.tsx";

const IntentToWorkflowCreator: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editingWorkflow = location.state?.workflow;
  const isEdit = !!editingWorkflow;

  // --- STATE ---
  const [activeStep, setActiveStep] = useState(isEdit ? 1 : 0);
  const [intent, setIntent] = useState<string>("");

  const [isDrafting, setIsDrafting] = useState(false);
  const [generatedWorkflow, setGeneratedWorkflow] = useState<any>(
    isEdit ? editingWorkflow : null,
  );
  const [workflowName, setWorkflowName] = useState<string>(
    isEdit ? (editingWorkflow?.name ?? "") : "",
  );

  const [isCreating, setIsCreating] = useState(false);
  const [notification, setNotification] = useState<any>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleDraftIntent = async () => {
    setIsDrafting(true);
    try {
      const response = await generateWorkflow(intent);
      // response shape: { name, dag_definition, intent_history, discovery?, validation? }
      setGeneratedWorkflow(response);
      setWorkflowName(response.name ?? "");
      // Keep `intent` in state so WorkflowRefinementEditor can pass it as original_intent
      // to POST /workflow/refine. Do NOT clear it.
      setActiveStep(1);
    } catch (error: any) {
      setNotification({
        open: true,
        message: `Workflow generation failed: ${error.message ?? "Unknown error"}`,
        severity: "error",
      });
    } finally {
      setIsDrafting(false);
    }
  };

  const handleCreateWorkflow = async () => {
    setIsCreating(true);
    try {
      if (isEdit) {
        // Edit mode: "Done" — just navigate back, refinements were already saved in-place
        navigate("/dashboard");
        return;
      }
      await createWorkflow(
        workflowName,
        generatedWorkflow?.dag_definition ?? generatedWorkflow,
        generatedWorkflow?.intent_history ?? null,
      );
      navigate("/dashboard");
    } catch (error: any) {
      setNotification({
        open: true,
        message: `Create failed: ${error.message ?? "Unknown error"}`,
        severity: "error",
      });
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Edit mode only: called by WorkflowRefinementEditor's chat send button.
   * Calls PUT /workflow/{id}/refine which refines the DAG via LLM and saves
   * a new version in one shot — no separate "Update Workflow" button press needed.
   */
  const handleInPlaceRefine = async (refinement: string): Promise<{ updatedDag: any; updatedName: string }> => {
    try {
      const result = await refineWorkflowInPlace(editingWorkflow.id, refinement);
      const updatedDag = result?.dag_definition ?? result;
      const updatedName = result?.name ?? workflowName;
      setGeneratedWorkflow({ dag_definition: updatedDag });
      setWorkflowName(updatedName);
      return { updatedDag, updatedName };
    } catch (error: any) {
      throw error;
    }
  };

  /**
   * Edit mode only: saves a name-only change via PUT /workflow/{id}.
   * Keeps the current DAG intact and just updates the name.
   */
  const handleSaveWorkflowName = async (newName: string): Promise<void> => {
    const currentDag = generatedWorkflow?.dag_definition ?? generatedWorkflow;
    await updateWorkflow(editingWorkflow.id, newName, currentDag);
    setWorkflowName(newName);
  };

  const handleCancel = () => {
    if (activeStep === 1 && !isEdit) {
      setActiveStep(0);
      setGeneratedWorkflow(null);
      setWorkflowName("");
    } else {
      navigate("/workflows");
    }
  };

  const dagDefinition = generatedWorkflow?.dag_definition ?? generatedWorkflow;

  return (
    <Box sx={{ flex: 1, overflow: "auto", px: 3, pt: 2 }}>
      <Box>
        <Typography variant="h4">
          {isEdit ? "Edit Workflow" : "Create Workflow"}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, px: 0.5 }}
        >
          {isEdit
            ? "Update your workflow's name or regenerate the DAG from a new intent."
            : "Describe what you want the workflow to do and we'll generate the DAG for you."}
        </Typography>
      </Box>

      {/* STEPPER */}
      {!isEdit && (
        <Box
          sx={{ mb: 4, mt: 3, display: "flex", justifyContent: "flex-start" }}
        >
          <Stepper
            activeStep={activeStep}
            sx={{ width: "100%", maxWidth: "500px" }}
          >
            <Step>
              <StepLabel>Draft</StepLabel>
            </Step>
            <Step>
              <StepLabel>Review</StepLabel>
            </Step>
          </Stepper>
        </Box>
      )}

      {/* STEP 0: DEFINE INTENT */}
      {!isEdit && activeStep === 0 && (
        <Fade in={activeStep === 0}>
          <Box>
            <Paper
              elevation={0}
              sx={{
                px: 3,
                py: 2,
                mb: 2,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
              }}
            >
              <Card
                sx={{
                  p: 2,
                  borderRadius: 4,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, ml: 1, fontWeight: 700 }}
                >
                  Workflow Intent
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  placeholder="E.g., 'Run a research agent to gather information on a given topic, then produce a polished report ready for sharing.'"
                  value={intent}
                  onChange={(e) => setIntent(e.target.value)}
                  variant="filled"
                  sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                />
              </Card>
            </Paper>
            <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="large"
                variant="contained"
                color="primary"
                onClick={handleDraftIntent}
                disabled={isDrafting || !intent.trim()}
                startIcon={
                  isDrafting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <AccountTreeOutlinedIcon />
                  )
                }
                sx={{
                  borderRadius: 99,
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  boxShadow: "0 10px 30px rgba(25, 118, 210, 0.3)",
                }}
              >
                {isDrafting ? "Generating..." : "Generate"}
              </Button>
            </Box>
          </Box>
        </Fade>
      )}

      {/* STEP 1: REVIEW GENERATED WORKFLOW */}
      {(isEdit || activeStep === 1) && generatedWorkflow && (
        <Fade in>
          <Box sx={{ mt: isEdit ? 3 : 0 }}>
            <WorkflowRefinementEditor
              workflowName={workflowName}
              onWorkflowNameChange={setWorkflowName}
              dagDefinition={dagDefinition}
              isEdit={isEdit}
              isCreating={isCreating}
              onConfirm={handleCreateWorkflow}
              onCancel={handleCancel}
              intent={intent}
              onIntentChange={setIntent}
              initialIntentHistory={generatedWorkflow?.intent_history ?? null}
              onDagRefined={(updatedDag, updatedName, intentHistory) => {
                setGeneratedWorkflow((prev: any) => ({
                  ...prev,
                  dag_definition: updatedDag,
                  ...(intentHistory ? { intent_history: intentHistory } : {}),
                }));
                setWorkflowName(updatedName);
              }}
              // In edit mode: each refinement is saved immediately via PUT /workflow/{id}/refine
              onRefinementRequested={isEdit ? handleInPlaceRefine : undefined}
              // In edit mode: allow saving a name-only change via PUT /workflow/{id}
              onWorkflowNameSave={isEdit ? handleSaveWorkflowName : undefined}
            />
          </Box>
        </Fade>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IntentToWorkflowCreator;
